import { createContext, useContext, useMemo } from "react";
import { IconButton, styled } from "~/common/mui";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

export interface Node {
  key: string;
  children: Node[];
}

export interface TreeProps<T extends Node> {
  className?: string;
  expanded?: string[];
  onToggleExpandItem: (key: string, expanded: boolean) => void;
  selected?: string[];
  onSelectItem: (key: T, selected: boolean) => void;
  // multiSelect?: boolean;
  items?: T[];
  renderItem?: (item: T, depth: number) => React.ReactNode;
}

const Root = styled("div")({
  display: "flex",
  flexDirection: "column",
});

const Item = styled("div", {
  shouldForwardProp: (prop) => prop !== "depth" && prop !== "selected",
})<{ depth: number; selected?: boolean }>(({ theme, depth, selected }) => ({
  paddingLeft: `calc(${depth} * ${theme.spacing(2)})`,
  display: "flex",
  alignItems: "center",
  ...(selected && {
    background: theme.palette.surface.main,
    color: theme.palette.primary.main,
    fontWeight: "bold",
    position: "relative",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: theme.spacing(0.5),
      background: theme.palette.primary.main,
    },
  }),
  "&:has(.fcc-tree-data-container:hover)": {
    background: theme.palette.action.hover,
  },
}));

const ButtonContainer = styled("div")({
  aspectRatio: "1",
  display: "flex",
  placeContent: "center",
  "&[hidden]": {
    display: "flex !important",
    visibility: "hidden",
  },
});

const DataContainer = styled("div")(({ theme }) => ({
  flex: 1,
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  display: "flex",
  cursor: "pointer",
}));

const DEFAULT_RENDER_ITEM = <T extends Node>(item: T, depth: number) => <div>{item.key}</div>;

export const Tree = <T extends Node>({
  items,
  renderItem = DEFAULT_RENDER_ITEM<T>,
  className,
  expanded,
  onToggleExpandItem,
  selected,
  onSelectItem,
}: TreeProps<T>) => {
  const ctxVal = useMemo(
    () => ({ expanded, onToggleExpandItem, selected, onSelectItem }),
    [expanded, onToggleExpandItem, selected, onSelectItem],
  );
  if (!items) {
    return null;
  }

  return (
    // @ts-ignore
    <Context.Provider value={ctxVal}>
      <Root className={className}>
        {items.map((item) => (
          <TreeItem key={item.key} item={item} depth={0} renderItem={renderItem} />
        ))}
      </Root>
    </Context.Provider>
  );
};

type ContextProps = Pick<TreeProps<Node>, "expanded" | "onToggleExpandItem" | "selected" | "onSelectItem">;

const Context = createContext<ContextProps>({
  expanded: [],
  onToggleExpandItem: () => {},
  selected: [],
  onSelectItem: () => {},
});

interface TreeItemProps<T extends Node> {
  item: T;
  renderItem: (item: T, depth: number) => React.ReactNode;
  depth: number;
}
const TreeItem = <T extends Node>({ item, renderItem, depth }: TreeItemProps<T>) => {
  const { expanded, selected, onToggleExpandItem, onSelectItem } = useContext(Context);
  const isExpanded = expanded?.includes(item.key);
  const isSelected = selected?.includes(item.key);

  return (
    <>
      <Item depth={depth} selected={isSelected}>
        <ButtonContainer hidden={item.children.length === 0}>
          <IconButton color={"primary"} onClick={() => onToggleExpandItem(item.key, !isExpanded)}>
            {isExpanded ? (
              <KeyboardArrowUpIcon  />
            ) : (
              <KeyboardArrowDownIcon  />
            )}
          </IconButton>
        </ButtonContainer>
        <DataContainer className="fcc-tree-data-container" onClick={() => onSelectItem(item, !isSelected)}>
          {renderItem(item, depth)}
        </DataContainer>
      </Item>
      {isExpanded &&
        item.children.map((child) => (
          <TreeItem key={child.key} item={child as T} depth={depth + 1} renderItem={renderItem} />
        ))}
    </>
  );
};
